<script lang="ts">
  import { onMount } from "svelte"
  import { Trophy } from "lucide-svelte"
  import { t, getTextDirection } from "$lib/utils/i18n"
  import { statsStore } from "$lib/stores/statsStore"
  import { defaultColorSettings } from "$lib/utils/colors"

  interface Props {
    competition: {
      slug: string
      language?: string
      informationCollection?: {
        teamOptions?: string[]
      }
      widgetSettings?: {
        leaderboardSettings?: {
          showTotalEntrants?: boolean
          showUserEntriesOnLeaderboard?: boolean
          showUserWinPercentage?: boolean
          showEntriesAheadOfNext?: boolean
          groupByTeam?: boolean
        }
      }
      widgetCustomizations?: {
        colorSettings?: typeof defaultColorSettings
      }
    }
    user?: {
      email: string
      userTeam?: string
    }
  }

  let { competition, user }: Props = $props()

  // Get text direction for RTL support
  const textDirection = $derived(getTextDirection(competition?.language))
  const isRTL = $derived(textDirection === "rtl")

  type LeaderboardEntry = {
    participant_name: string
    participant_team?: string | null
    total_entries: number
    current_user?: boolean
    user_rank?: number
  }

  type TeamLeaderboardEntry = {
    team_name: string
    participant_count: number
    total_entries: number
    referral_count: number
  }

  let leaderboard: LeaderboardEntry[] = $state([])
  let teamLeaderboard: TeamLeaderboardEntry[] = $state([])
  let isLoading = $state(true)
  let error = $state(false)

  // Computed property that merges fetched team data with all available team options
  const completeTeamLeaderboard = $derived(() => {
    if (!isTeamMode || !competition.informationCollection?.teamOptions) {
      return teamLeaderboard
    }

    const teamOptions = competition.informationCollection.teamOptions
    const existingTeams = new Map(
      teamLeaderboard.map((team) => [team.team_name, team]),
    )

    // Create complete team list with all teams from options
    const completeTeams = teamOptions.map((teamName) => {
      const existingTeam = existingTeams.get(teamName)
      return (
        existingTeam || {
          team_name: teamName,
          participant_count: 0,
          total_entries: 0,
          referral_count: 0,
        }
      )
    })

    // Sort by total_entries descending, then by team name for consistent ordering
    return completeTeams.sort((a, b) => {
      if (b.total_entries !== a.total_entries) {
        return b.total_entries - a.total_entries
      }
      return a.team_name.localeCompare(b.team_name)
    })
  })

  // Check if team grouping is enabled
  const isTeamMode = $derived(
    competition?.widgetSettings?.leaderboardSettings?.groupByTeam ?? false,
  )

  // Get color settings with defaults
  const colorSettings = $derived(
    competition?.widgetCustomizations?.colorSettings || defaultColorSettings,
  )

  async function fetchLeaderboard() {
    try {
      isLoading = true
      const response = await fetch(`/api/competitions/leaderboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug: competition.slug,
          page: 1,
          pageSize: 10,
          public: true,
          userEmail: user?.email || null,
          groupByTeam: isTeamMode,
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to fetch leaderboard")
      }

      const { data } = await response.json()
      if (isTeamMode) {
        teamLeaderboard = data
      } else {
        leaderboard = data
      }
    } catch (err) {
      console.error("Failed to fetch leaderboard:", err)
      error = true
    } finally {
      isLoading = false
    }
  }

  onMount(() => {
    fetchLeaderboard()
  })

  // Refetch when team mode changes
  $effect(() => {
    if (
      competition?.widgetSettings?.leaderboardSettings?.groupByTeam !==
      undefined
    ) {
      fetchLeaderboard()
    }
  })
</script>

{#if !error}
  <div
    class="rounded-lg p-4"
    style="background-color: {colorSettings.leaderboardDetailsBg}; direction: {textDirection};"
  >
    {#if (competition?.widgetSettings?.leaderboardSettings?.showUserWinPercentage ?? true) && user && !isTeamMode && leaderboard.length > 0}
      {@const userEntry = leaderboard.find((entry) => entry.current_user)}
      {#if userEntry}
        {@const winPercentage = (
          (Number($statsStore.yourEntries) / Number($statsStore.totalEntries)) *
          100
        ).toFixed(2)}
        <div
          class="mb-4 p-3 border rounded-lg"
          style="background-color: {colorSettings.winPercentageBg}; border-color: {colorSettings.winPercentageBorder};"
        >
          <div
            class="flex items-center justify-between"
            class:flex-row-reverse={isRTL}
          >
            <div>
              <span
                class="text-sm font-medium"
                style="color: {colorSettings.winPercentageText};"
                >{t("widget_leaderboard_your_chance_of_winning")}</span
              >
              <br />
              <span
                class="text-xs mt-1"
                style="color: {colorSettings.winPercentageText};"
              >
                {t("widget_leaderboard_odds_calculation")}
              </span>
            </div>
            <div>
              <span
                class="text-lg font-bold"
                style="color: {colorSettings.winPercentageText};"
                >{winPercentage}%</span
              >
            </div>
          </div>
        </div>
      {/if}
    {/if}

    <div class="flex items-center gap-2 mb-4">
      <Trophy class="h-5 w-5" style="color: {colorSettings.iconColor};" />
      <h3
        class="text-lg font-semibold"
        style="color: {colorSettings.primaryTextColor};"
      >
        {isTeamMode
          ? t("widget_leaderboard_top_teams")
          : t("widget_leaderboard_top_participants")}
      </h3>
    </div>

    {#if isLoading}
      <!-- Loading skeleton -->
      <div class="space-y-3">
        {#each [1, 2, 3] as _, i}
          <div
            class="flex items-center justify-between animate-pulse"
            class:flex-row-reverse={isRTL}
          >
            <div class="flex items-center gap-3" class:flex-row-reverse={isRTL}>
              <div class="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div class="h-4 bg-gray-200 rounded w-32"></div>
            </div>
            <div class="h-4 bg-gray-200 rounded w-16"></div>
          </div>
        {/each}
      </div>
    {:else if isTeamMode ? completeTeamLeaderboard().length === 0 : leaderboard.length === 0}
      <p
        class="text-sm text-center py-4"
        style="color: {colorSettings.secondaryTextColor};"
      >
        {isTeamMode
          ? t("widget_leaderboard_no_teams")
          : t("widget_leaderboard_no_participants")}
      </p>
    {:else}
      <div class="space-y-3">
        {#if isTeamMode}
          {#each completeTeamLeaderboard() as entry, index}
            {@const isUserTeam =
              user?.userTeam && entry.team_name === user.userTeam}
            <div
              class="flex items-center justify-between rounded-lg p-2 transition-colors"
              style={isUserTeam
                ? `background-color: ${colorSettings.leaderboardItemBg}; border: 2px solid ${colorSettings.leaderboardWinnerBorder};`
                : `background-color: ${colorSettings.backgroundColor};`}
            >
              <div
                class="flex items-center gap-3"
                class:flex-row-reverse={isRTL}
              >
                <div class="flex items-center justify-center w-8 h-8">
                  {#if index === 0}
                    <span class="text-2xl">🥇</span>
                  {:else if index === 1}
                    <span class="text-2xl">🥈</span>
                  {:else if index === 2}
                    <span class="text-2xl">🥉</span>
                  {:else}
                    <span
                      class="text-sm font-semibold w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center"
                      style="color: {colorSettings.secondaryTextColor};"
                    >
                      {index + 1}
                    </span>
                  {/if}
                </div>
                <div>
                  <p
                    class="text-sm font-medium truncate max-w-[150px]"
                    style="color: {colorSettings.primaryTextColor};"
                  >
                    {entry.team_name}
                  </p>
                  <p
                    class="text-xs truncate max-w-[150px]"
                    style="color: {colorSettings.secondaryTextColor};"
                  >
                    {entry.participant_count}
                    {entry.participant_count === 1
                      ? "participant"
                      : "participants"}
                  </p>
                  {#if isUserTeam}
                    <p
                      class="text-xs"
                      style="color: {colorSettings.secondaryTextColor};"
                    >
                      {t("widget_leaderboard_your_team")}
                    </p>
                  {/if}
                </div>
              </div>
              <div class="text-right">
                <p
                  class="text-sm font-semibold"
                  style="color: {colorSettings.primaryTextColor};"
                >
                  {entry.total_entries}
                </p>
                <p
                  class="text-xs"
                  style="color: {colorSettings.secondaryTextColor};"
                >
                  {t("widget_action_entries", {
                    count: Number(entry.total_entries),
                  })}
                </p>
              </div>
            </div>
          {/each}
        {:else}
          {#each leaderboard as entry, index}
            <div
              class="flex items-center justify-between rounded-lg p-2 transition-colors"
              style={entry.current_user
                ? `background-color: ${colorSettings.leaderboardItemBg}; border: 2px solid ${colorSettings.leaderboardWinnerBorder};`
                : `background-color: ${colorSettings.backgroundColor};`}
            >
              <div
                class="flex items-center gap-3"
                class:flex-row-reverse={isRTL}
              >
                <div class="flex items-center justify-center w-8 h-8">
                  {#if entry.user_rank}
                    <!-- Show actual rank if it exists (for user outside top 10) -->
                    <span
                      class="text-sm font-semibold w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center"
                      style="color: {colorSettings.secondaryTextColor};"
                    >
                      {entry.user_rank}
                    </span>
                  {:else if index === 0}
                    <span class="text-2xl">🥇</span>
                  {:else if index === 1}
                    <span class="text-2xl">🥈</span>
                  {:else if index === 2}
                    <span class="text-2xl">🥉</span>
                  {:else}
                    <span
                      class="text-sm font-semibold w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center"
                      style="color: {colorSettings.secondaryTextColor};"
                    >
                      {index + 1}
                    </span>
                  {/if}
                </div>
                <div>
                  <p
                    class="text-sm font-medium truncate max-w-[150px]"
                    style="color: {colorSettings.primaryTextColor};"
                  >
                    {entry.participant_name}
                  </p>
                  {#if entry.participant_team}
                    <p
                      class="text-xs truncate max-w-[150px]"
                      style="color: {colorSettings.secondaryTextColor};"
                    >
                      {entry.participant_team}
                    </p>
                  {/if}
                  {#if entry.current_user && entry.user_rank && entry.user_rank > 10}
                    <p
                      class="text-xs"
                      style="color: {colorSettings.secondaryTextColor};"
                    >
                      {t("widget_leaderboard_your_position")}
                    </p>
                  {/if}
                </div>
              </div>
              <div class="text-right">
                {#if competition?.widgetSettings?.leaderboardSettings?.showUserEntriesOnLeaderboard ?? true}
                  <p
                    class="text-sm font-semibold"
                    style="color: {colorSettings.primaryTextColor};"
                  >
                    {entry.total_entries}
                  </p>
                  <p
                    class="text-xs"
                    style="color: {colorSettings.secondaryTextColor};"
                  >
                    {t("widget_action_entries", {
                      count: Number(entry.total_entries),
                    })}
                  </p>
                  {#if entry.current_user && (competition?.widgetSettings?.leaderboardSettings?.showEntriesAheadOfNext ?? true) && index < leaderboard.length - 1}
                    {@const nextEntry = leaderboard[index + 1]}
                    {@const entriesAhead =
                      Number(entry.total_entries) -
                      Number(nextEntry.total_entries)}
                    {#if entriesAhead > 0}
                      <p class="text-xs font-medium text-green-600">
                        +{entriesAhead}
                        {t("widget_leaderboard_ahead")}
                      </p>
                    {/if}
                  {/if}
                {:else}
                  <p
                    class="text-sm font-semibold"
                    style="color: {colorSettings.primaryTextColor};"
                  >
                    #{entry.user_rank || index + 1}
                  </p>
                  <p
                    class="text-xs"
                    style="color: {colorSettings.secondaryTextColor};"
                  >
                    {t("widget_leaderboard_rank")}
                  </p>
                {/if}
              </div>
            </div>
          {/each}
        {/if}
      </div>
    {/if}
  </div>
{/if}
